import {
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableSortLabel,
} from "@mui/material";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { useSelector } from "react-redux";
import { useMemo } from "react";
import { useScopes } from "../../../../../../STOperationhooks/useScopes";

interface BulkActionModalProps {
  open: boolean;
  onClose: () => void;
}

const BulkActionModal = ({ open, onClose }: BulkActionModalProps) => {
  const { removeNegativeSearchTerm } = useScopes();
  const { selectedRowsForNegatives, singleRowIDPassToModal } = useSelector(
    (state: any) => state.searchTermOperation
  );
  const records = useMemo(() => {
    if (!selectedRowsForNegatives) return [];
    return singleRowIDPassToModal
      ? selectedRowsForNegatives.filter(
          (row: any) => row.id === singleRowIDPassToModal
        )
      : selectedRowsForNegatives;
  }, [selectedRowsForNegatives, singleRowIDPassToModal]);

  // 🔹 Grouped data according to adgroup_id and total count
  const { groupedDataByAdgroup, totalRemovedCount } = useMemo(() => {
    const grouped = Object.values(
      (records || []).reduce((acc: any, row: any) => {
        if (!acc[row.adgroup_id]) {
          acc[row.adgroup_id] = {
            adgroup_id: row.adgroup_id,
            adgroup_name: row.adgroup_name, // keep the name
            removedCount: 0,
            search_terms: [],
          };
        }

        acc[row.adgroup_id].removedCount += 1;
        acc[row.adgroup_id].search_terms.push(row.search_term);
        return acc;
      }, {} as Record<string, any>)
    );

    if (grouped.length === 0) {
      // handle empty case here
      return {
        groupedDataByAdgroup: [],
        totalRemovedCount: 0,
        totalAdgroup: 0,
      };
    }

    const total = grouped.reduce(
      (sum: number, group: any) => sum + group.removedCount,
      0
    ); // total removed count(Search Terms)
    const totalAdgroup = grouped.length; // total adgroup

    return {
      groupedDataByAdgroup: grouped,
      totalRemovedCount: total,
      totalAdgroup: totalAdgroup,
    };
  }, [records]);

  // close modal and clear selected rows and single row id
  const handleCloseNegativeModal = () => {
    onClose();
  };

  // remove negative terms from adgroups
  const removedNegativeTerms = async () => {
    const sharedUids = records?.map((item: any) => item.shared_uid) || [];
    if (sharedUids.length === 0) return;
    const reqBody = {
      terms: sharedUids.map((uid: string) => ({
        shared_uid: uid,
        keep_in_ad_groups: false,
      })),
    };

    removeNegativeSearchTerm(reqBody, handleCloseNegativeModal, "adgroups");
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseNegativeModal}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Total Number of Search Terms Removed from Adgroups: {totalRemovedCount}
      </DialogTitle>
      <DialogContent sx={{ padding: "0px 20px 16px" }}>
        <TableContainer component={Paper} elevation={0}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel>Adgroup Name</TableSortLabel>
                </TableCell>
                <TableCell align="left">
                  <TableSortLabel>
                    {totalRemovedCount} of Search Terms Removed from Adgroups
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {groupedDataByAdgroup?.map((row: any, index) => (
                <TableRow key={index}>
                  <TableCell>{row.adgroup_name}</TableCell>
                  <TableCell align="left">{row.removedCount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          sx={{
            backgroundColor: "#FFF4E5",
            padding: "8px 16px",
            borderRadius: "4px",
            border: "1px solid #ed6c02",
            width: "auto",
            marginTop: 2,
          }}
        >
          <Typography
            align="center"
            color="#ed6c02"
            display="flex"
            alignItems="center"
          >
            <ErrorOutlineOutlinedIcon style={{ marginRight: 5 }} />
            Search Terms are removed from Adgroup, not from the List
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "0px 16px 20px 16px" }}>
        <Button
          // disabled={removeNegativeLoading}
          onClick={handleCloseNegativeModal}
          color="secondary"
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          data-tracker="505"
          disabled={records?.length === 0}
          onClick={removedNegativeTerms}
          color="secondary"
          variant="contained"
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkActionModal;
